/**
 * Dashboard container (B3 shell; C19 figures). Card order and permission filter follow original/src/App.tsx:1636-1651
 * (default order) and :2630-2666 (`allowedDashboardOrder`), with the user's `ui.dashboard_order` /
 * `ui.dashboard_hidden` preferences (D-124). Figures (C19): `GET /dashboard` — ledger sums of the vault (own + custodies,
 * D-118), dollar vault, digital boxes (with / without today's commission, D-074), capital, customer debt / trust, expenses
 * and the operation counters. **C26 (D-104):** the profit card, the expenses card and the capital card read the figures the
 * server takes from the Profits screen's read model (today, this month, since the last settlement — D-099) and the D-019
 * owner formula; the profit card opens the breakdown ("تفاصيل الأرباح"), the capital card the capital history with the
 * add / withdraw / capitalize dialogs, the expenses card the expenses tab, the debt / trust cards the customer list
 * filtered (App.tsx:26880-26935, 26968-26971, 17979-17988, 28022). Nothing is computed here (ADR-02 §2.9): the container
 * formats strings and picks the USD / other-currency lines the old card showed only when positive (`showExchangeRate &&
 * … > 0`). The deposit / withdraw quick actions open the quick operation (C19); the others wait for their domain.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Dashboard, ProfitBreakdownModal, formatWithCommas, type DashboardCard, type DashboardCurrencyFigure } from "@faylasuf/ui";
import type { DashboardProfit } from "../api/types.js";
import { CapitalHistoryContainer } from "./capital/CapitalHistoryContainer.js";
import { useCapitalActions } from "./capital/CapitalActions.js";
import { fmt, isZero } from "./customers/format.js";
import { useApp, useSession } from "../app/context.js";
import { isSessionLost } from "../app/errors.js";
import type { UiPrefs } from "../app/preferences.js";
import { keys } from "../app/query.js";
import type { SectionKey } from "../app/sections.js";

/** App.tsx:1636-1651 default order without the two `operatorSubSafe*` cards (custody cards, B4 / D-118). */
export const DASHBOARD_DEFAULT_ORDER = ["totalOwed", "totalCredit", "monthlyExpenses", "totalProfit", "inventory", "installments", "vault", "totalDigital", "dollarVault", "currenciesVault", "capital", "count"] as const;
export type DashboardCardId = (typeof DASHBOARD_DEFAULT_ORDER)[number];
type CardId = DashboardCardId;
const DEFAULT_ORDER = DASHBOARD_DEFAULT_ORDER;

/** App.tsx:2630-2666 — which cards the viewer may see. */
export function dashboardCardAllowed(id: DashboardCardId, hasPermission: (t: string) => boolean): boolean {
  switch (id) {
    case "totalOwed":
    case "totalCredit":
      return hasPermission("debts") && hasPermission("view_debts_and_trusts");
    case "monthlyExpenses":
    case "totalProfit":
      return hasPermission("financials");
    case "inventory":
      return hasPermission("store");
    case "installments":
      return hasPermission("installments");
    case "count":
      return hasPermission("dashboard");
    case "vault":
      return hasPermission("view_main_vault_iqd") || hasPermission("view_main_vault_usd") || hasPermission("wallets");
    case "totalDigital":
      return hasPermission("wallets");
    case "dollarVault":
      return hasPermission("dollar");
    case "currenciesVault":
      return hasPermission("currencies");
    case "capital":
      return hasPermission("invested_capital");
  }
}

/** Default order overlaid with the viewer's `ui.dashboard_order` (D-124); unfiltered. */
export function fullDashboardOrder(dashboardOrder: readonly string[] | null): DashboardCardId[] {
  const order: DashboardCardId[] = [];
  for (const k of dashboardOrder ?? []) if ((DEFAULT_ORDER as readonly string[]).includes(k)) order.push(k as DashboardCardId);
  for (const k of DEFAULT_ORDER) if (!order.includes(k)) order.push(k);
  return order;
}

/** i18n label key per card, as the old lists card resolved them (App.tsx:13386-13409). */
export const DASHBOARD_CARD_LABEL_KEY: Readonly<Record<DashboardCardId, string>> = {
  totalOwed: "totalOwed",
  totalCredit: "totalCredit",
  monthlyExpenses: "monthlyExpenses",
  totalProfit: "totalProfit",
  inventory: "inventory",
  installments: "dashboard.installments",
  vault: "vault",
  totalDigital: "totalDigital",
  dollarVault: "dollarVault",
  currenciesVault: "nav.currencies",
  capital: "capital",
  count: "dailyCount",
};

const ZERO = "0";

/**
 * App.tsx:1687-1734 `dashboardConfig` defaults: every card is `md` / 1 column except the profit card (`lg`, 2 columns).
 * The old per-card config (size, colSpan, visible) lived in localStorage; its default is the only state the new
 * app has until the dashboard editor arrives (B4, D-124).
 */
const DEFAULT_CARD_CONFIG: Readonly<Partial<Record<CardId, { size: "sm" | "md" | "lg"; colSpan: 1 | 2 }>>> = {
  totalProfit: { size: "lg", colSpan: 2 },
};

/** A positive figure of a currency, or undefined (the old card's `… > 0` test). */
const positiveOf = (m: Readonly<Record<string, string>> | undefined, cur: string): string | undefined => {
  const v = m?.[cur];
  return v && !isZero(v) && !v.startsWith("-") ? fmt(v) : undefined;
};
/** The currencies other than IQD / USD with a positive figure (old `currencyProfits` lines). */
const othersOf = (m: Readonly<Record<string, string>> | undefined): DashboardCurrencyFigure[] =>
  Object.entries(m ?? {})
    .filter(([c, v]) => c !== "IQD" && c !== "USD" && !isZero(v) && !v.startsWith("-"))
    .map(([currency, v]) => ({ currency, value: fmt(v) }));

export type DashboardTarget = { readonly section: "financials"; readonly tab: "expenses" } | { readonly section: "debts"; readonly filter: "debt" | "trust" };

export function DashboardPage({ prefs, onComingB4, onQuickTx, onOpenSection, onOpenTarget, subSafesActive = false }: { prefs: UiPrefs; onComingB4: () => void; onQuickTx: (type: "deposit" | "withdraw") => void; onOpenSection: (section: SectionKey) => void; onOpenTarget?: (target: DashboardTarget) => void; subSafesActive?: boolean }) {
  const { hasPermission, sessionLost } = useSession();
  const { t, source } = useApp();
  const summary = useQuery({ queryKey: keys.dashboard, queryFn: () => source.dashboard(), enabled: hasPermission("dashboard"), staleTime: 10_000 });
  if (summary.error && isSessionLost(summary.error)) sessionLost();
  const d = summary.data;
  const p: DashboardProfit | null = d?.profit ?? null;
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [capitalOpen, setCapitalOpen] = useState(false);
  const usdShown = !!p && p.pending["USD"] !== undefined; // USD hidden (D-066): the server leaves USD out
  const capital = useCapitalActions(p ? { IQD: fmt(p.pending["IQD"] ?? "0"), USD: usdShown ? fmt(p.pending["USD"] ?? "0") : null } : null, subSafesActive);
  // App.tsx:26853 etc.: `${formatNumber(x)} د.ع` — the unit travels inside the string (StatCard splits it into its pill).
  const iqd = (v: string | undefined) => `${formatWithCommas(v ?? ZERO)} ${t("unit.iqd")}`;
  const IQD = iqd(undefined);

  const allowed = (id: CardId): boolean => dashboardCardAllowed(id, hasPermission);

  const order = fullDashboardOrder(prefs.dashboardOrder);
  const hidden = new Set(prefs.dashboardHidden);

  const cards: DashboardCard[] = order
    .filter((id) => allowed(id) && !hidden.has(id))
    .map((id): DashboardCard => {
      const base = { onClick: onComingB4, ...(prefs.icons[id] ? { customIcon: prefs.icons[id]! } : {}), ...(DEFAULT_CARD_CONFIG[id] ?? {}) };
      switch (id) {
        case "totalOwed":
          return { ...base, ...(onOpenTarget ? { onClick: () => onOpenTarget({ section: "debts", filter: "debt" }) } : {}), kind: "totalOwed", value: iqd(d?.totalOwedIqd), ...(d && d.totalOwedUsd !== "0" ? { usdValue: formatWithCommas(d.totalOwedUsd) } : {}), lastMovement: IQD };
        case "totalCredit":
          return { ...base, ...(onOpenTarget ? { onClick: () => onOpenTarget({ section: "debts", filter: "trust" }) } : {}), kind: "totalCredit", value: iqd(d?.totalCreditIqd), ...(d && d.totalCreditUsd !== "0" ? { usdValue: formatWithCommas(d.totalCreditUsd) } : {}), lastMovement: IQD };
        case "monthlyExpenses":
          return { ...base, ...(onOpenTarget ? { onClick: () => onOpenTarget({ section: "financials", tab: "expenses" }) } : {}), kind: "monthlyExpenses", value: iqd(d?.monthlyExpensesIqd), totalExpenses: iqd(d?.totalExpensesIqd) };
        case "totalProfit": {
          // D-104 / D-099: pending (all time), today, this month, since the last settlement; the latest capitalization and withdrawal
          const cap = p?.lastCapitalization ?? {};
          const wd = p?.lastWithdrawal ?? {};
          const opt = (k: string, v: string | undefined) => (v ? { [k]: v } : {});
          const others = (k: string, v: DashboardCurrencyFigure[]) => (v.length ? { [k]: v } : {});
          return {
            ...base,
            onClick: () => setBreakdownOpen(true),
            kind: "totalProfit",
            pendingProfit: fmt(p?.pending["IQD"] ?? ZERO),
            ...opt("pendingProfitUsd", positiveOf(p?.pending, "USD")),
            ...others("pendingOther", othersOf(p?.pending)),
            netDailyProfit: fmt(p?.daily.net["IQD"] ?? ZERO),
            ...opt("netDailyProfitUsd", positiveOf(p?.daily.net, "USD")),
            ...others("dailyOther", othersOf(p?.daily.net)),
            grossDailyProfit: fmt(p?.daily.gross["IQD"] ?? ZERO),
            ...opt("grossDailyProfitUsd", positiveOf(p?.daily.gross, "USD")),
            capitalizedProfit: cap["IQD"] ? fmt(cap["IQD"].amount) : ZERO,
            ...opt("capitalizedProfitUsd", cap["USD"] ? fmt(cap["USD"].amount) : undefined),
            ...opt("lastWithdrawnProfit", wd["IQD"] ? fmt(wd["IQD"].amount) : undefined),
            ...opt("lastWithdrawnProfitUsd", wd["USD"] ? fmt(wd["USD"].amount) : undefined),
            netMonthlyProfit: fmt(p?.monthly.net["IQD"] ?? ZERO),
            ...opt("netMonthlyProfitUsd", positiveOf(p?.monthly.net, "USD")),
            ...others("monthlyOther", othersOf(p?.monthly.net)),
            grossMonthlyProfit: fmt(p?.monthly.gross["IQD"] ?? ZERO),
            ...opt("grossMonthlyProfitUsd", positiveOf(p?.monthly.gross, "USD")),
            totalProfit: fmt(p?.sinceSettlement.net["IQD"] ?? ZERO),
            ...opt("totalProfitUsd", positiveOf(p?.sinceSettlement.net, "USD")),
          } as DashboardCard;
        }
        case "inventory":
          return { ...base, kind: "inventory", value: IQD };
        case "installments":
          return { ...base, kind: "installments", value: IQD, activeContracts: ZERO };
        case "count":
          return { ...base, kind: "count", monthly: d?.counts.monthly ?? ZERO, weekly: d?.counts.weekly ?? ZERO, daily: d?.counts.daily ?? ZERO };
        case "vault":
          return { ...base, onClick: () => onOpenSection("wallets"), kind: "vault", value: iqd(d?.vaultIqd) };
        case "totalDigital":
          return { ...base, onClick: () => onOpenSection("wallets"), kind: "totalDigital", value: iqd(d?.totalDigitalWithoutCommission), inclCommission: iqd(d?.totalDigital) };
        case "dollarVault":
          return { ...base, kind: "dollarVault", value: formatWithCommas(d?.vaultUsd ?? ZERO) };
        case "currenciesVault":
          return { ...base, kind: "currenciesVault", value: formatWithCommas(d?.currencyStockIqd ?? ZERO) };
        case "capital":
          // App.tsx:27999-28024: invested capital (D-019 owner formula) and the initial-capital line; the card opens the
          // capital history (`handleShowCapitalHistory`), whose toolbar carries the add / withdraw / capitalize buttons.
          return { ...base, onClick: () => setCapitalOpen(true), kind: "capital", value: iqd(d?.investedCapital ?? undefined), initialCapital: iqd(d?.initialCapital ?? undefined) };
      }
    });

  const nameOr = (n: string | null) => n ?? t("pf.main_vault");
  const abs = (v: string) => fmt(v.replace(/^-/, ""));
  const breakdown = p ? (
    <ProfitBreakdownModal
      wallets={p.breakdown.byBox.filter((r) => !(r.currency === "USD" && !usdShown)).map((r) => ({ name: nameOr(r.name), amount: fmt(r.amount), usd: r.currency === "USD" }))}
      operators={p.breakdown.byOperator.map((r) => ({ name: r.name ?? t("pf.unknown"), amount: fmt(r.amount), usd: r.currency === "USD" }))}
      expenses={p.breakdown.expensesByOperator.map((r) => ({ name: r.name || t("pf.unknown"), amount: fmt(r.amount), usd: false }))}
      net={p.breakdown.netByOperator.map((r) => ({ name: r.name ?? t("pf.unknown"), usd: r.currency === "USD", positive: !r.net.startsWith("-"), net: r.net.startsWith("-") ? `-${abs(r.net)}` : `+${abs(r.net)}`, gross: fmt(r.gross), expenses: fmt(r.expenses) }))}
      otherCurrencies={[]}
      onClose={() => setBreakdownOpen(false)}
    />
  ) : null;

  return (
    <>
    <Dashboard
      iconSize={prefs.iconSize}
      cardSize={prefs.cardSize}
      quickActions={{
        onWithdraw: () => onQuickTx("withdraw"),
        onDeposit: () => onQuickTx("deposit"),
        onManualWork: onComingB4,
        ...(hasPermission("transfers_cash") ? { onTransfersCash: onComingB4 } : {}),
        ...(hasPermission("store") ? { onStoreSell: onComingB4 } : {}),
      }}
      cards={cards}
    />
    {breakdownOpen && breakdown}
    {capitalOpen && <CapitalHistoryContainer actions={capital} onClose={() => setCapitalOpen(false)} />}
    {capital.dialogs}
    </>
  );
}
